import {
  ButtonColor,
  ButtonShape,
  cn,
  Divider,
  DropdownButton,
  DropdownItem,
  Hypertext,
  HypertextSize,
  Icon,
  IconName,
  IconSize,
  IconVariant,
  Popover,
  Tooltip,
  useModalConfirmation,
  useToastManager
} from "@quortex/front-common"
import { useNavigate, useSearch } from "@tanstack/react-location"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { Handle, type NodeProps, Position } from "@xyflow/react"
import type React from "react"
import { Fragment, memo, useCallback, useMemo } from "react"
import { DeleteSegmentContent } from "@/components/DeleteSegmentContent"
import { SegmentTypeToIcon } from "@/const"
import {
  formatBandwidth,
  formatListWithLimit,
  formatPercentage,
  getFormatedRegionNameMixed
} from "@/helpers/formatters"
import { useWorkspacePath } from "@/hooks/navigation"
import type { SegmentationExpandedGenerics } from "@/pages/ContentSteering/Segmentation"
import { useDeleteSegment, useSegmentList } from "@/queries/segments"
import { useOverallCdnStatistics } from "@/queries/statistics"
import { useSegmentWizardStore } from "@/stores/segmentWizard"
import type { BalancingWithCdnStatus, SegmentResponse } from "@/types/api"

export type NodeData = {
  uuid: string
  parentUuid?: string | null
  label: string
  type: SegmentResponse["type"]
  children: string[]
  cdnList: BalancingWithCdnStatus[]
  isLastChild?: boolean
  isHighlighted?: boolean
  isFallback?: boolean
  network?: SegmentResponse["network"]
  devices?: SegmentResponse["devices"]
  protocol?: SegmentResponse["protocol"]
  regions?: (api.switchns.CountryValue["code"] | api.switchns.SubdivisionValue["code"])[]
  ref?: (element: HTMLElement | null) => void
}

interface SegmentationNodeProps extends NodeProps {
  data: NodeData
}

const isPercentageBalancing = (
  cdn: BalancingWithCdnStatus
): cdn is Extract<BalancingWithCdnStatus, { percentage: number }> => "percentage" in cdn

const isCapacityBalancing = (
  cdn: BalancingWithCdnStatus
): cdn is Extract<BalancingWithCdnStatus, { capacity: number }> => "capacity" in cdn

const SegmentationNode = (props: SegmentationNodeProps) => {
  const { data } = props

  const isHighlighted = data.isHighlighted ?? true

  const { openConfirmationModal } = useModalConfirmation()
  const workspacePath = useWorkspacePath()
  const { setSegmentWizardState, clearWizard } = useSegmentWizardStore()
  const navigate = useNavigate()
  const toastManager = useToastManager()
  const { data: segments } = useSuspenseQuery(useSegmentList())
  const search = useSearch<SegmentationExpandedGenerics>()

  const isInPercentageMode = useMemo(
    () => data.cdnList.some(cdn => isPercentageBalancing(cdn) && cdn.percentage !== undefined),
    [data.cdnList]
  )

  const { data: statistics } = useSuspenseQuery({
    ...useOverallCdnStatistics({ unit: isInPercentageMode ? "percentage" : "bandwidth" })
  })

  const { mutateAsync: deleteSegment } = useMutation(
    useDeleteSegment({
      segmentUuid: data.uuid
    })
  )

  const estimatedUsedBandwidthPerCdn = useMemo(
    () =>
      statistics.bandwidthPerCdn?.[statistics.bandwidthPerCdn?.length - 1]?.repartition?.reduce(
        (map, cdnStat) => {
          if (cdnStat.cdnUuid) {
            map[cdnStat.cdnUuid] = cdnStat.estimatedUsedBandwidth
          }
          return map
        },
        {} as Record<string, number>
      ) || {},
    [statistics.bandwidthPerCdn]
  )

  const handleDeleteSegment = useCallback(async () => {
    try {
      await deleteSegment()
      toastManager.success("Segment deleted")
    } catch (_error) {}
  }, [deleteSegment, toastManager])

  const toolTipText = useMemo(
    () =>
      ({
        device: formatListWithLimit(data.devices),
        device_fallback: formatListWithLimit(data.devices),
        geolocation: getFormatedRegionNameMixed(data.regions),
        geolocation_fallback: getFormatedRegionNameMixed(data.regions),
        network: formatListWithLimit(data.network?.asns?.map(asn => asn.organization)),
        network_fallback: formatListWithLimit(data.network?.asns?.map(asn => asn.organization)),
        protocol: "",
        protocol_fallback: "",
        root: ""
      }) as const,
    [data.devices, data.regions, data.network]
  )

  const handleSegmentClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      navigate({
        to: `${workspacePath}/segmentation/segment/${data.uuid}`
      })
    },
    [navigate, workspacePath, data.uuid]
  )

  const handleAddSubSegment = useCallback(() => {
    clearWizard()
    setSegmentWizardState({
      parentUuid: data.uuid
    })
    navigate({
      replace: true,
      search: {
        modal: "add_segment",
        step: "selectSegmentType",
        view: search.view === "segmentationExpanded" ? "segmentationExpanded" : undefined
      }
    })
  }, [clearWizard, setSegmentWizardState, data.uuid, navigate, search.view])

  const handleDeleteClick = useCallback(() => {
    openConfirmationModal({
      cancelBtnDatatestId: "cancel-btn",
      cancelBtnLabel: "Cancel",
      children: (
        <DeleteSegmentContent
          isLastSegment={data.isLastChild}
          isRootSegment={data.type === "root"}
          name={data.label}
          parentSegmentName={segments.find(segment => segment.uuid === data.parentUuid)?.name ?? ""}
          restSegmentName={
            segments.find(segment => segment.isFallback && segment.parentUuid === data.parentUuid)
              ?.name ?? ""
          }
          segmentChildren={data.children}
        />
      ),
      okBtnDatatestId: "delete-btn",
      okBtnLabel: "Delete segment",
      onAccept: handleDeleteSegment,
      title: "Delete segment?",
      variant: "destructive"
    })
  }, [
    openConfirmationModal,
    data.isLastChild,
    data.type,
    data.label,
    data.parentUuid,
    data.children,
    segments,
    handleDeleteSegment
  ])

  const dropdownBody = useMemo(
    () => (
      <>
        <DropdownItem
          className="justify-start! flex items-center gap-xs"
          data-testid="properties-btn"
          to={`${workspacePath}/segmentation/segment/${data.uuid}`}
          type="link"
        >
          <Icon icon={IconName.SLIDER} size={IconSize.MEDIUM} type={IconVariant.SOLID} />
          Properties
        </DropdownItem>
        {data.children.length === 0 && (
          <DropdownItem
            className="justify-start! flex items-center gap-xs"
            data-testid="add-sub-segment-btn"
            type="button"
            onClick={handleAddSubSegment}
          >
            <Icon icon={IconName.PLUS} size={IconSize.MEDIUM} type={IconVariant.SOLID} />
            Add sub segment
          </DropdownItem>
        )}
        <Divider />
        <Tooltip
          body="To ensure all viewers are covered, fallback segments cannot be removed"
          enabled={data.isFallback && !data.type.includes("network")}
          placement="top"
          wrapperClassName="w-full"
        >
          <DropdownItem
            className="justify-start! flex items-center gap-xs text-red-600"
            data-testid="delete-segment-btn"
            disabled={data.isFallback}
            type="button"
            onClick={handleDeleteClick}
          >
            <Icon icon={IconName.TRASH} size={IconSize.MEDIUM} type={IconVariant.SOLID} />
            Delete
          </DropdownItem>
        </Tooltip>
      </>
    ),
    [
      workspacePath,
      data.uuid,
      data.children.length,
      data.type,
      data.isFallback,
      handleAddSubSegment,
      handleDeleteClick
    ]
  )

  return (
    <>
      {data.type !== "root" && (
        <Handle
          className="h-2 w-2 border-2 border-blue-300 bg-white"
          id="input"
          position={Position.Left}
          type="target"
        />
      )}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: clickable non-interactive element */}
      <div
        ref={data.ref}
        className={cn(
          isHighlighted
            ? "border-2 border-yellow-300 bg-yellow-50"
            : "border border-grey-300 bg-white",
          "radius-sm z-10 flex h-20 w-52 flex-col justify-center gap-xs p-sm hover:cursor-pointer hover:border-transparent hover:bg-blue-50 hover:outline-3 hover:outline-blue-300"
        )}
        role="presentation"
        onClick={handleSegmentClick}
      >
        <div className="flex flex-row gap-x-xs">
          <div className="flex grow flex-col gap-y-xs">
            <div className="typo-sm-medium flex w-full items-center gap-xs">
              <Icon
                className="text-blue-500"
                icon={SegmentTypeToIcon[data.type]}
                size={IconSize.SMALL}
                type={IconVariant.SOLID}
              />
              <Tooltip
                body={data.type && toolTipText[data.type]}
                className="first-letter:uppercase"
                enabled={!!toolTipText[data.type]}
                placement="top"
              >
                <div className="line-clamp-1 text-clip break-all text-left">
                  {data.type === "root" ? "All viewers" : data.label || " "}
                </div>
              </Tooltip>
            </div>
            {data.cdnList.length > 0 && (
              <div className="flex items-center justify-start text-xs">
                <Popover
                  body={
                    <div className="w-full">
                      <div className="grid w-full grid-cols-3 gap-x-sm">
                        <div className="col-span-2 mb-2xs flex items-center justify-start">CDN</div>
                        <div className="flex items-center justify-end">
                          {isInPercentageMode ? "%" : "Gbps"}
                        </div>

                        {data.cdnList?.map(cdn => {
                          const percentage = isPercentageBalancing(cdn) ? (cdn.percentage ?? 0) : 0
                          const capacity = isCapacityBalancing(cdn) ? cdn.capacity : undefined
                          const hasValue = isInPercentageMode ? percentage != null : capacity !== 0

                          return (
                            <Fragment key={cdn.cdnUuid}>
                              <Hypertext
                                key={cdn.cdnUuid}
                                as="button"
                                className="flex grow py-2xs"
                                data-testid={`segment-node-cdn-link-${cdn.cdnUuid}`}
                                size={HypertextSize.SMALL}
                                onClick={() => {
                                  navigate({
                                    search: {
                                      from: "segmentation"
                                    },
                                    to: `${workspacePath}/cdn/${cdn.cdnUuid}`
                                  })
                                }}
                              >
                                <div className="line-clamp-1 overflow-hidden text-clip break-all text-left">
                                  {cdn.cdnName}
                                </div>
                              </Hypertext>
                              <div className="flex flex-row items-center justify-start gap-2xs text-orange-800">
                                {(cdn.qoeStatus === "WARNING" || cdn.qoeStatus === "ERROR") && (
                                  <>
                                    <Icon
                                      className="text-orange-800"
                                      icon={IconName.WARNING}
                                      size={IconSize.MEDIUM}
                                      type={IconVariant.SOLID}
                                    />

                                    <span className="inline-flex text-orange-800">QoE Warning</span>
                                  </>
                                )}
                              </div>
                              <div className="flex grid-cols-2 flex-row items-center justify-end gap-xs">
                                {hasValue ? (
                                  <div className="radius relative flex h-2 w-12 max-w-80 items-center justify-between rounded-lg border border-grey-300 bg-grey-200">
                                    <div
                                      className="h-full rounded-full bg-yellow-300"
                                      style={{
                                        width: `${isInPercentageMode ? percentage : estimatedUsedBandwidthPerCdn && capacity && estimatedUsedBandwidthPerCdn[cdn.cdnUuid] !== undefined ? ((estimatedUsedBandwidthPerCdn[cdn.cdnUuid] ?? 0) / capacity) * 100 : 0}%`
                                      }}
                                    ></div>
                                  </div>
                                ) : (
                                  <div>&nbsp;</div>
                                )}
                                <div className="flex items-center justify-end">
                                  {isInPercentageMode && formatPercentage(percentage)}
                                  {!isInPercentageMode &&
                                    estimatedUsedBandwidthPerCdn &&
                                    `${estimatedUsedBandwidthPerCdn && formatBandwidth(estimatedUsedBandwidthPerCdn[cdn.cdnUuid] ?? 0)}${capacity !== 0 && capacity !== undefined ? `/${capacity}` : ""}`}
                                </div>
                              </div>
                            </Fragment>
                          )
                        })}
                      </div>
                    </div>
                  }
                  className="w-88"
                  description="string"
                  enabled={data.cdnList.length > 0}
                  hoverProps={{
                    delay: { close: 200, open: 300 }
                  }}
                  placement="bottom"
                  showTransitions
                  trigger="hover"
                >
                  <div className={cn("flex items-center", data.cdnList.length === 0 && "hidden")}>
                    <span className="text-grey-600">
                      {data.cdnList.length} CDN{data.cdnList.length > 1 ? "s" : ""}
                    </span>
                    {data.cdnList.find(cdn => cdn.qoeStatus === "WARNING") && (
                      <Icon
                        className="ml-xs inline-flex text-orange-800"
                        icon={IconName.WARNING}
                        size={IconSize.MEDIUM}
                        type={IconVariant.SOLID}
                      />
                    )}
                  </div>
                </Popover>
              </div>
            )}
          </div>
          <div className="flex items-center justify-center text-xs">
            <DropdownButton
              body={dropdownBody}
              color={ButtonColor.TERTIARY}
              data-testid="more-options-btn"
              floatingClassName="z-[10001]"
              hideCaret
              shape={ButtonShape.ICON}
              size={HypertextSize.SMALL}
              type="button"
              usePortal
              onDoubleClick={event => {
                event.preventDefault()
                event.stopPropagation()
              }}
            >
              <Icon icon={IconName.ELLIPSIS} size={IconSize.SMALL} type={IconVariant.SOLID} />
            </DropdownButton>
          </div>
        </div>
      </div>
      {data.children.length > 0 && (
        <Handle
          className="h-2 w-2 border-2 border-blue-300 bg-white"
          id="output"
          position={Position.Right}
          type="source"
        />
      )}
    </>
  )
}

export default memo(SegmentationNode)
